import { type ProjectSubmissionAttachmentSummary } from 'ContentProgress';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile } from '@fortawesome/pro-solid-svg-icons/faFile';
import { memo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Text } from 'Text';
import {
    SUBMISSION_FORMAT_PDF,
    SUBMISSION_FORMAT_SPREADSHEET,
    SUBMISSION_FORMAT_ZIP,
    SUBMISSION_FORMAT_PRESENTATION_LINK,
    SUBMISSION_FORMAT_GITHUB_LINK,
} from './constants';

const FILE_FORMAT_TYPES = new Set([SUBMISSION_FORMAT_PDF, SUBMISSION_FORMAT_SPREADSHEET, SUBMISSION_FORMAT_ZIP]);

function getLinkTranslationKey(formatType: string): string {
    if (formatType === SUBMISSION_FORMAT_PRESENTATION_LINK) return 'submittedPresentationLink';
    if (formatType === SUBMISSION_FORMAT_GITHUB_LINK) return 'submittedGithubLink';
    return 'submittedGenericLink';
}

const FileAttachment: FC<{ attachment: ProjectSubmissionAttachmentSummary }> = ({ attachment }) => (
    <div className="flex items-baseline gap-1">
        <FontAwesomeIcon icon={faFile} className="-ml-0.5 text-xs text-slate-grey" aria-hidden="true" />
        <Text className="text-sm text-slate-grey">{attachment.originalFilename}</Text>
    </div>
);

const LinkAttachment: FC<{ attachment: ProjectSubmissionAttachmentSummary }> = ({ attachment }) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'learnerProjects.learnerProjects' });

    return (
        <a
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-coral hover:underline"
        >
            {t(getLinkTranslationKey(attachment.submissionFormatType))}
        </a>
    );
};

const ProjectSubmissionAttachmentsListComponent: FC<{
    attachments: ProjectSubmissionAttachmentSummary[];
}> = ({ attachments }) => {
    if (attachments.length === 0) return null;

    return (
        <div className="flex flex-col gap-1">
            {attachments.map(attachment =>
                FILE_FORMAT_TYPES.has(attachment.submissionFormatType) ? (
                    <FileAttachment key={attachment.url} attachment={attachment} />
                ) : (
                    <LinkAttachment key={attachment.url} attachment={attachment} />
                ),
            )}
        </div>
    );
};

export const ProjectSubmissionAttachmentsList = memo(ProjectSubmissionAttachmentsListComponent);
